/**
 * Map structured backend error codes to short user-facing messages.
 *
 * The auth endpoints return `{detail: {code, message}}` for handled
 * cases. We prefer our own messages because the backend messages are
 * intentionally generic ("Invalid email or password.") and we may
 * want a slightly different tone per surface.
 */

import { ApiError } from '../../api/client';

const CODE_TO_MESSAGE: Record<string, string> = {
  invalid_credentials: 'Email or password is incorrect.',
  account_pending: 'Your account is awaiting administrator approval.',
  account_rejected: 'Your signup was declined. Contact your administrator.',
  account_disabled: 'This account has been disabled. Contact your administrator.',
  email_taken: 'An account with this email already exists.',
  invalid_token: 'This reset link is invalid or has expired. Request a new one.',
  wrong_current_password: 'Current password is incorrect.',
  must_change_password: 'You must change your password before continuing.',
  missing_role: 'You do not have permission for that section.',
};

/** Map an unknown error into a user-friendly string. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const known = err.code ? CODE_TO_MESSAGE[err.code] : undefined;
    if (known) return known;
    if (err.serverMessage) return err.serverMessage;
    return `Something went wrong (HTTP ${err.status}).`;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}
